
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Footer from '../../../components/feature/Footer';
import TableOfContents, { generateTocItems } from '../../../components/feature/TableOfContents';
import ArticlePartOne from './components/ArticlePartOne';
import ArticlePartTwo from './components/ArticlePartTwo';
import ArticlePartThree from './components/ArticlePartThree';
import ArticlePartFour from './components/ArticlePartFour';

const tocItems = generateTocItems([
  'El Paso Housing Market Snapshot: 2025 Overview',
  'El Paso Home Value Trends and Appreciation History',
  'El Paso Neighborhood Market Breakdown',
  'Inventory and Supply Analysis',
  'New Construction Impact on El Paso Market',
  'Price Per Square Foot Analysis',
  'Rental Market and Investment Outlook',
  'Mortgage Rates and Affordability Impact',
  'Foreclosure and Distressed Property Data',
  'Economic Drivers Supporting El Paso Housing',
  'Market Forecast: Where El Paso Is Heading',
  'Frequently Asked Questions El Paso Housing Market',
]);

export default function MarketDataElPasoTexas() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              src="https://public.readdy.ai/ai/img_res/c0e6113e-28a2-41ed-b2dc-22e3f9d83ef9.png"
              alt="Cash For Houses El Paso"
              className="h-12 w-auto"
            />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/#how-it-works" className="text-sm font-medium text-gray-700 hover:text-black transition-colors whitespace-nowrap cursor-pointer">How It Works</Link>
            <Link to="/#about-elpaso" className="text-sm font-medium text-gray-700 hover:text-black transition-colors whitespace-nowrap cursor-pointer">About El Paso</Link>
            <Link to="/#benefits" className="text-sm font-medium text-gray-700 hover:text-black transition-colors whitespace-nowrap cursor-pointer">Benefits</Link>
            <Link to="/get-cash" className="text-sm font-medium text-gray-700 hover:text-black transition-colors whitespace-nowrap cursor-pointer">Blog</Link>
            <Link to="/#faq" className="text-sm font-medium text-gray-700 hover:text-black transition-colors whitespace-nowrap cursor-pointer">FAQ</Link>
            <a href="tel:9157457883" className="px-6 py-2.5 bg-black text-white text-sm font-semibold rounded-full hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer">Call: 915-745-7883</a>
          </div>
          <div className="md:hidden">
            <a href="tel:9157457883" className="px-4 py-2 bg-black text-white text-sm font-semibold rounded-full whitespace-nowrap cursor-pointer">Call Now</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/get-cash" className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-black transition-colors mb-8 cursor-pointer">
            <i className="ri-arrow-left-line"></i>
            Back to Blog
          </Link>

          <div className="mb-8">
            <span className="inline-block px-4 py-1.5 bg-[#D4AF37] text-white text-xs uppercase font-semibold rounded-full mb-4">MARKET DATA</span>
            <h1 className="text-5xl md:text-6xl font-bold text-black mb-6 leading-tight">
              El Paso, Texas Housing Market Data &amp; Trends (2025)
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-6">
              Comprehensive overview of home values, neighborhood pricing, inventory levels, appreciation trends, and market forecasts for El Paso real estate.
            </p>
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <i className="ri-user-line"></i>
                <span>Cash For Houses El Paso</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-time-line"></i>
                <span>22 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-calendar-line"></i>
                <span>Updated July 2025</span>
              </div>
            </div>
          </div>

          <div className="rounded-3xl overflow-hidden shadow-xl mb-12 h-96">
            <img
              src="https://public.readdy.ai/ai/img_res/a438147eda03b19c7eed2856e7146c40.jpg"
              alt="El Paso Texas housing market data overview with neighborhoods and Franklin Mountains"
              title="El Paso TX housing market data and trends 2025 comprehensive overview"
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Key Stats Banner */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 text-center">
              <p className="text-2xl font-bold text-black">$274K</p>
              <p className="text-xs text-gray-500 mt-1">Median Home Price</p>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 text-center">
              <p className="text-2xl font-bold text-black">+1.5%</p>
              <p className="text-xs text-gray-500 mt-1">Annual Appreciation</p>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 text-center">
              <p className="text-2xl font-bold text-black">48 Days</p>
              <p className="text-xs text-gray-500 mt-1">Avg Days on Market</p>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 text-center">
              <p className="text-2xl font-bold text-black">$142</p>
              <p className="text-xs text-gray-500 mt-1">Price Per Sq Ft</p>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-6 py-16" itemScope itemType="https://schema.org/Article">
        <meta itemProp="headline" content="El Paso Texas Housing Market Data and Trends 2025" />
        <meta itemProp="author" content="Cash For Houses El Paso" />

        <TableOfContents items={tocItems} />

        <ArticlePartOne />
        <ArticlePartTwo />
        <ArticlePartThree />
        <ArticlePartFour />
      </article>

      {/* Call to Action */}
      <section className="bg-[#2C2C2C] py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Need to Sell Your El Paso Home Fast?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Whether the market data has you concerned or you simply need a quick, guaranteed sale, we buy houses for cash in any condition. No inspections, no appraisals, no waiting.
          </p>
          <a href="tel:9157457883" className="inline-block text-5xl font-bold text-white mb-8 hover:text-[#D4AF37] transition-colors cursor-pointer">
            915-745-7883
          </a>
          <div>
            <Link to="/#contact-form" className="inline-block px-12 py-4 bg-white text-black text-lg font-semibold rounded-full hover:scale-105 transition-transform whitespace-nowrap cursor-pointer">
              Get Your Cash Offer Now
            </Link>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-black mb-12">Related Articles</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <article className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300">
              <Link to="/get-cash/sell-house-fast-el-paso" className="cursor-pointer">
                <div className="h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=professional%20real%20estate%20transaction%20with%20sold%20sign%20in%20front%20of%20modern%20El%20Paso%20home%2C%20bright%20sunny%20day%2C%20successful%20home%20sale%20celebration%20with%20keys%20and%20documents%2C%20warm%20welcoming%20atmosphere%20photography&width=600&height=400&seq=fast-sale-related-003&orientation=landscape"
                    alt="Sell house fast El Paso"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-[#D4AF37] text-white text-xs uppercase font-semibold rounded-full mb-3">SELLING TIPS</span>
                  <h3 className="text-lg font-semibold text-black mb-2">How to Sell Your House Fast in El Paso</h3>
                  <p className="text-sm text-gray-600">The fastest methods to sell your El Paso home for cash.</p>
                </div>
              </Link>
            </article>

            <article className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300">
              <Link to="/get-cash/financing-house-el-paso-tx" className="cursor-pointer">
                <div className="h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=professional%20mortgage%20broker%20or%20loan%20officer%20reviewing%20home%20financing%20documents%20with%20first%20time%20homebuyers%20at%20modern%20desk%2C%20calculator%20and%20financial%20paperwork%20spread%20out%20showing%20loan%20approval%20process%2C%20warm%20welcoming%20office%20environment%20with%20natural%20lighting%2C%20El%20Paso%20Texas%20real%20estate%20financing%20consultation%2C%20optimistic%20atmosphere%20of%20successful%20home%20purchase%20journey&width=800&height=500&seq=financing-hero-ep-002&orientation=landscape"
                    alt="Financing house El Paso TX"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-[#D4AF37] text-white text-xs uppercase font-semibold rounded-full mb-3">FINANCING GUIDE</span>
                  <h3 className="text-lg font-semibold text-black mb-2">Quick Guide to Financing Your House in El Paso, TX</h3>
                  <p className="text-sm text-gray-600">Every loan type and assistance program for El Paso buyers.</p>
                </div>
              </Link>
            </article>

            <article className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300">
              <Link to="/get-cash/why-house-not-selling-el-paso" className="cursor-pointer">
                <div className="h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=frustrated%20homeowner%20looking%20at%20for%20sale%20sign%20in%20front%20yard%20that%20has%20been%20there%20too%20long%2C%20El%20Paso%20Texas%20residential%20neighborhood%20with%20desert%20landscape%2C%20worried%20expression%20checking%20phone%20for%20updates%2C%20warm%20afternoon%20sunlight%20casting%20long%20shadows%2C%20professional%20real%20estate%20photography%20capturing%20selling%20frustration&width=800&height=500&seq=not-selling-hero-001&orientation=landscape"
                    alt="Why house not selling El Paso"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-[#D4AF37] text-white text-xs uppercase font-semibold rounded-full mb-3">SELLING CHALLENGES</span>
                  <h3 className="text-lg font-semibold text-black mb-2">Why Isn&apos;t My House Selling in El Paso?</h3>
                  <p className="text-sm text-gray-600">3 hidden reasons and how to fix it fast.</p>
                </div>
              </Link>
            </article>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
